import { ArrowUpRight, HeartHandshake, MessagesSquare, Umbrella, Brain } from "lucide-react";
import { Reveal } from "@/components/site/reveal";
import { scrollToSection } from "@/components/site/Nav";

const SERVICES = [
  {
    n: "01",
    icon: HeartHandshake,
    title: "Personal Care",
    body: "Bathing, dressing, grooming, mobility support and daily routines — delivered patiently, privately, and with complete respect.",
  },
  {
    n: "02",
    icon: MessagesSquare,
    title: "Companionship",
    body: "Conversation, walks, games, errands and shared meals. Care that brightens the whole day, not just the checklist.",
  },
  {
    n: "03",
    icon: Umbrella,
    title: "Respite Care",
    body: "Trusted cover for family caregivers — a few hours or a few days, whenever you need to rest, work, or simply breathe.",
  },
  {
    n: "04",
    icon: Brain,
    title: "Dementia & Memory Care",
    body: "Calm, consistent routines from caregivers specially trained in memory-loss support, keeping loved ones safe at home.",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-silver-light/50 border-y border-silver py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-navy" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-navy">What we do</span>
            </div>
            <h2 className="font-display font-light tracking-tight leading-[1.05] text-navy-dark text-4xl md:text-6xl">
              Care, tailored to <em className="italic text-navy">your family</em>
            </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-navy-dark/70">
            Every plan is built around one person and reviewed with the family each month.
            No contracts, no minimum hours.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-px bg-silver border border-silver">
          {SERVICES.map((s, i) => {
            const dark = i === 0 || i === 3;
            return (
            <Reveal key={s.n} delay={i * 0.06} className="h-full">
              <button
                data-testid={`service-card-${s.n}`}
                onClick={() => scrollToSection("contact")}
                className={`group relative w-full h-full text-left p-10 md:p-14 transition-colors duration-500 ${
                  dark ? "bg-navy-dark hover:bg-navy" : "bg-white hover:bg-silver-light"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className={`font-mono text-xs tracking-[0.3em] ${dark ? "text-silver" : "text-navy"}`}>{s.n}</span>
                  <ArrowUpRight className={`w-5 h-5 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${
                    dark ? "text-silver/50 group-hover:text-white" : "text-navy-dark/30 group-hover:text-navy"
                  }`} />
                </div>
                <s.icon className={`w-8 h-8 mt-10 ${dark ? "text-silver" : "text-navy"}`} strokeWidth={1.5} />
                <h3 className={`mt-6 font-display font-light text-3xl md:text-4xl tracking-tight ${dark ? "text-white" : "text-navy-dark"}`}>
                  {s.title}
                </h3>
                <p className={`mt-5 text-base leading-relaxed max-w-md ${dark ? "text-white/70" : "text-navy-dark/70"}`}>{s.body}</p>
              </button>
            </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <p data-testid="services-note" className="text-xs font-mono uppercase tracking-[0.25em] text-navy-dark/50">
            Select a service to start a conversation — or simply tell us your story below
          </p>
        </Reveal>
      </div>
    </section>
  );
}
